import {
  getLcTransfertWorkflowPhaseLabel,
  LC_STOCK_TRANSFERT_IS_RECEPTION,
} from '../document/stock-transfer-workflow.util';
import type { EntrepotTableRow } from './logiciel-caisse-entrepot.service';
import type { StockTransfertTableChildRow, StockTransfertTableRow } from './logiciel-caisse-stock-transfert.service';

/** Affichage liste : nom d’entrepôt + code entre parenthèses. */
export function formatEntrepotNomEtCode(
  nom: unknown,
  code: unknown
): string {
  const n = nom != null ? String(nom).trim() : '';
  const c = code != null ? String(code).trim() : '';
  if (n && c) {
    if (n.toLowerCase() === c.toLowerCase()) {
      return n;
    }
    return `${n} (${c})`;
  }
  return n || c;
}

function entrepotLookupKey(value: string): string {
  return value.trim().toLowerCase();
}

/** Index code (et id) → libellé / code pour enrichir les colonnes Départ / Réception. */
export function buildEntrepotCodeLibelleMap(
  erpRows: EntrepotTableRow[]
): Map<string, { libelle: string; code: string }> {
  const map = new Map<string, { libelle: string; code: string }>();
  for (const row of erpRows || []) {
    const code = String(row['CODE ENTREPÔT'] ?? '').trim();
    const libelle = String(row.LIBELLÉ ?? '').trim();
    const id = String(row.id ?? '').trim();
    const entry = { libelle, code: code || libelle };
    if (code) {
      map.set(entrepotLookupKey(code), entry);
    }
    if (libelle) {
      map.set(entrepotLookupKey(libelle), entry);
    }
    if (id) {
      map.set(`id:${id}`, entry);
    }
  }
  return map;
}

export interface EntrepotStackedDisplay {
  nom: string;
  code: string;
}

function stackedDisplayFromLcEntrepotFields(
  libelle: unknown,
  code: unknown,
  fallbackCell: unknown
): EntrepotStackedDisplay | null {
  const nom = libelle != null ? String(libelle).trim() : '';
  const c = code != null ? String(code).trim() : '';
  if (!nom && !c) {
    return null;
  }
  const displayNom = nom || c || String(fallbackCell ?? '').trim() || '—';
  const displayCode = c && c.toLowerCase() !== displayNom.toLowerCase() ? c : '';
  return { nom: displayNom, code: displayCode };
}

export function resolveEntrepotStackedFromCell(
  cell: unknown,
  lookup: Map<string, { libelle: string; code: string }>
): EntrepotStackedDisplay {
  const v = cell != null ? String(cell).trim() : '';
  if (!v || v === '—') {
    return { nom: '—', code: '' };
  }
  const hit =
    lookup.get(entrepotLookupKey(v)) ??
    lookup.get(`id:${v}`);
  if (!hit) {
    return { nom: v, code: '' };
  }
  const code = (hit.code || v).trim();
  const nom = (hit.libelle || code || v).trim();
  const showCode =
    code !== '' && code.toLowerCase() !== nom.toLowerCase();
  return {
    nom: nom || v,
    code: showCode ? code : '',
  };
}

/** Nom + code (sous le nom) pour colonnes Départ / Réception — ne modifie pas DÉPART / RÉCEPTION. */
export function enrichTransfertTableRowsEntrepots(
  rows: StockTransfertTableRow[],
  erpRows: EntrepotTableRow[]
): void {
  if (!rows.length) {
    return;
  }
  const lookup = buildEntrepotCodeLibelleMap(erpRows ?? []);
  for (const row of rows) {
    const dep =
      stackedDisplayFromLcEntrepotFields(
        row.entrepot_depart_libelle,
        row.entrepot_depart_code,
        row.DÉPART
      ) ?? resolveEntrepotStackedFromCell(row.DÉPART, lookup);
    const rec =
      stackedDisplayFromLcEntrepotFields(
        row.entrepot_reception_libelle,
        row.entrepot_reception_code,
        row.RÉCEPTION
      ) ?? resolveEntrepotStackedFromCell(row.RÉCEPTION, lookup);
    row._entrepot_depart_nom = dep.nom;
    row._entrepot_depart_code = dep.code;
    row._entrepot_reception_nom = rec.nom;
    row._entrepot_reception_code = rec.code;
  }
}

/** Libellé phase workflow LC (`is_reception` sur stock_transfert). */
export function stockTransfertIsReceptionLabel(code: unknown, circulationCree?: unknown): string {
  return getLcTransfertWorkflowPhaseLabel(code, circulationCree);
}

function formatDateTransfertDisplay(value: unknown): string {
  if (value == null || value === '') {
    return '—';
  }
  if (typeof value === 'string' && value.includes('/') && !value.includes('T')) {
    return value;
  }
  const d = value instanceof Date ? value : new Date(String(value));
  if (Number.isNaN(d.getTime())) {
    return String(value);
  }
  return d.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
}

function formatQuantiteDisplay(value: unknown): string {
  if (value == null || value === '') {
    return '—';
  }
  const n =
    typeof value === 'number'
      ? value
      : parseFloat(String(value).replace(',', '.').replace(/[^\d.-]/g, ''));
  if (!Number.isFinite(n)) {
    return String(value);
  }
  if (Number.isInteger(n)) {
    return String(n);
  }
  return n.toFixed(3).replace(/\.?0+$/, '');
}

function entrepotLabel(
  libelle: unknown,
  code: unknown,
  entrepotId: unknown,
  fallbackFromMapped: unknown
): string {
  const combined = formatEntrepotNomEtCode(libelle, code);
  if (combined) {
    return combined;
  }
  const fromMapped = fallbackFromMapped != null ? String(fallbackFromMapped).trim() : '';
  if (fromMapped && fromMapped !== '—') {
    return fromMapped;
  }
  const id = Number(entrepotId);
  if (Number.isFinite(id) && id > 0) {
    return `Entrepôt #${id}`;
  }
  return '—';
}

function readIsReceptionCode(raw: Record<string, unknown>): number {
  const value = raw['is_reception'] ?? raw['_is_reception'];
  if (value === undefined || value === null || value === '') {
    return 0;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

/** Ligne API by-user-station (brute ou déjà mappée tableau POS). */
export function mapStockTransfertApiItemToTableRow(raw: Record<string, unknown>): StockTransfertTableRow {
  const isRec = readIsReceptionCode(raw);
  const id = Number(raw.id);
  return {
    id: Number.isFinite(id) ? id : 0,
    'N° DOCUMENT': String(raw.num_document ?? raw['N° DOCUMENT'] ?? '—').trim() || '—',
    'DATE TRANSFERT': formatDateTransfertDisplay(
      raw.date_transfert ?? raw['DATE TRANSFERT']
    ),
    'QUANTITÉ': formatQuantiteDisplay(raw.quantite ?? raw['QUANTITÉ']),
    DÉPART: entrepotLabel(
      raw.entrepot_depart_libelle,
      raw.entrepot_depart_code,
      raw.entrepot_id_depart,
      raw['DÉPART']
    ),
    RÉCEPTION: entrepotLabel(
      raw.entrepot_reception_libelle,
      raw.entrepot_reception_code,
      raw.entrepot_id_reception,
      raw['RÉCEPTION']
    ),
    STATUT: stockTransfertIsReceptionLabel(
      isRec,
      raw.circulation_cree ?? raw._circulation_cree
    ),
    NOTE: raw.note != null ? String(raw.note) : String(raw['NOTE'] ?? ''),
    _is_reception: isRec,
    _circulation_cree:
      raw._circulation_cree === true ||
      Number(raw._circulation_cree) === 1 ||
      raw.circulation_cree === true ||
      Number(raw.circulation_cree) === 1,
    isGroupParent: raw.isGroupParent === true,
    children: Array.isArray(raw.children)
      ? (raw.children as unknown as StockTransfertTableChildRow[])
      : undefined,
    entrepot_depart_libelle:
      raw.entrepot_depart_libelle != null ? String(raw.entrepot_depart_libelle) : null,
    entrepot_depart_code:
      raw.entrepot_depart_code != null ? String(raw.entrepot_depart_code) : null,
    entrepot_reception_libelle:
      raw.entrepot_reception_libelle != null ? String(raw.entrepot_reception_libelle) : null,
    entrepot_reception_code:
      raw.entrepot_reception_code != null ? String(raw.entrepot_reception_code) : null,
    entrepot_id_depart:
      raw.entrepot_id_depart != null ? Number(raw.entrepot_id_depart) : null,
    entrepot_id_reception:
      raw.entrepot_id_reception != null ? Number(raw.entrepot_id_reception) : null,
  };
}

function normalizeStockTransfertStatutLabel(value: string): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/** Phase LC « En circulation » (is_reception = 4 + circulation_cree ou libellé). */
export function isStockTransfertRowEnCirculation(row: StockTransfertTableRow): boolean {
  const statut = normalizeStockTransfertStatutLabel(String(row.STATUT || ''));
  if (statut === 'en circulation') {
    return true;
  }
  const code = Number(row._is_reception);
  if (code !== LC_STOCK_TRANSFERT_IS_RECEPTION.EN_COURS_LIVRAISON) {
    return false;
  }
  return row._circulation_cree === true || Number(row._circulation_cree) === 1;
}

/**
 * Périmètre onglet « Tous » : Créé, Annulé, Validé (sans En circulation).
 * Exclut brouillon (0), réception terminée (1), etc.
 */
export function isStockTransfertRowIncludedInTousTab(row: StockTransfertTableRow): boolean {
  if (isStockTransfertRowEnCirculation(row)) {
    return false;
  }
  const code = Number(row._is_reception);
  if (
    code === LC_STOCK_TRANSFERT_IS_RECEPTION.EN_COURS_TRANSFERT ||
    code === LC_STOCK_TRANSFERT_IS_RECEPTION.ANNULE
  ) {
    return true;
  }
  if (code === LC_STOCK_TRANSFERT_IS_RECEPTION.EN_COURS_LIVRAISON) {
    return true;
  }
  return false;
}
